import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { AffiliateBountyPaymentMethod, AffiliateBountyStatus, AffiliateCandidateOutcome, AffiliateCandidateOutcomeLabels, AffiliateCommissionStatus, AffiliateReferralType } from '@parkupp/core'
import { ToastrService } from 'ngx-toastr'
import { AffiliateBountyRow, AffiliateService } from '../../../services/affiliate.service'

type Tab = 'requests' | 'bounties' | 'parker' | 'building' | 'overlap' | 'integrity'

interface AffiliateTotal {
    affiliateUid: string
    name: string
    code: string
    invoices: number
    gross: number
    commission: number
    pending: number
    approved: number
    requested: number
    paid: number
}

interface BuildingOverlap {
    parkingKey: string
    building: string
    portfolio: string
    invoices: number
    landlordAffiliates: string[]
    parkerAffiliates: string[]
    paid: number
    avoided: number
}

const PAYING_STATUSES = [AffiliateCommissionStatus.PENDING, AffiliateCommissionStatus.APPROVED, AffiliateCommissionStatus.REQUESTED, AffiliateCommissionStatus.PAID]

@Component({
    selector: 'app-admin-affiliate-payouts',
    templateUrl: './admin-affiliate-payouts.component.html',
    styleUrls: ['./admin-affiliates.component.scss'],
})
export class AdminAffiliatePayoutsComponent implements OnInit {
    tab: Tab = 'requests'
    from: string
    to: string

    requests: any[] = []
    requestsLoading = true
    expanded = new Set<string>()
    requestFilter: 'open' | 'all' = 'open'

    reportLoading = false
    reportLoaded = false
    names: Record<string, { displayName: string; code: string }> = {}
    commissions: any[] = []

    integrityFrom: string
    integrity: { checkedInvoices: number; missing: string[]; stale: string[]; doubleEarned: string[]; fixed: number; bountiesCreated?: number } | null = null
    integrityRunning = false

    Outcome = AffiliateCandidateOutcome
    outcomeLabel(outcome: string): string {
        return AffiliateCandidateOutcomeLabels[outcome as AffiliateCandidateOutcome] || outcome
    }
    ReferralType = AffiliateReferralType

    constructor(private affiliateService: AffiliateService, private toastr: ToastrService, private route: ActivatedRoute, private router: Router) {
        const now = new Date()
        this.to = now.toISOString().slice(0, 10)
        this.from = new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString().slice(0, 10)
        this.integrityFrom = new Date(now.getFullYear() - 1, now.getMonth(), 1).toISOString().slice(0, 10)
    }

    ngOnInit(): void {
        const tab = this.route.snapshot.queryParamMap.get('tab') as Tab | null
        this.setTab(tab || 'requests')
        this.loadRequests()
        if (tab !== 'bounties') this.loadBounties()
    }

    setTab(tab: Tab) {
        this.tab = tab
        this.router.navigate([], { queryParams: { tab: tab === 'requests' ? null : tab }, replaceUrl: true })
        if (['parker', 'building', 'overlap'].includes(tab) && !this.reportLoaded) this.loadReport()
        if (tab === 'bounties' && !this.bountiesLoaded) this.loadBounties()
    }

    // ---- Security guard bounties ----
    bounties: AffiliateBountyRow[] = []
    bountiesLoaded = false
    bountyFilter: 'due' | 'all' = 'due'
    BountyStatus = AffiliateBountyStatus
    paymentMethods = Object.values(AffiliateBountyPaymentMethod)
    paying: AffiliateBountyRow | null = null
    payment = { method: AffiliateBountyPaymentMethod.CASH as AffiliateBountyPaymentMethod, reference: '', notes: '' }

    async loadBounties() {
        try {
            this.bounties = await this.affiliateService.adminBounties()
            this.bountiesLoaded = true
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not load bounties')
        }
    }

    get visibleBounties(): AffiliateBountyRow[] {
        return this.bountyFilter === 'due' ? this.bounties.filter((b) => b.status === AffiliateBountyStatus.DUE) : this.bounties
    }

    get bountiesDueTotal(): number {
        return this.bounties.filter((b) => b.status === AffiliateBountyStatus.DUE).reduce((sum, b) => sum + b.amount, 0)
    }

    get bountiesDueCount(): number {
        return this.bounties.filter((b) => b.status === AffiliateBountyStatus.DUE).length
    }

    openPay(b: AffiliateBountyRow) {
        this.paying = b
        this.payment = { method: AffiliateBountyPaymentMethod.CASH, reference: '', notes: '' }
    }

    async confirmPay() {
        if (!this.paying) return
        try {
            await this.affiliateService.adminBountyAction(this.paying.id, 'pay', {
                method: this.payment.method,
                reference: this.payment.reference.trim() || undefined,
                notes: this.payment.notes.trim() || undefined,
            })
            this.toastr.success(`R${this.paying.amount} to ${this.paying.affiliateName} marked as paid`)
            this.paying = null
            await this.loadBounties()
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not mark it paid')
        }
    }

    async bountyCancel(b: AffiliateBountyRow, action: 'cancel' | 'reopen') {
        let notes: string | undefined
        if (action === 'cancel') {
            const reason = prompt('Why is this bounty being cancelled?')
            if (reason === null) return
            notes = reason
        }
        try {
            await this.affiliateService.adminBountyAction(b.id, action, { notes })
            this.toastr.success(action === 'cancel' ? 'Bounty cancelled' : 'Bounty reopened')
            await this.loadBounties()
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not update the bounty')
        }
    }

    methodLabel(method: string | null): string {
        return method === 'eft' ? 'EFT' : method ? method[0].toUpperCase() + method.slice(1) : ''
    }

    generating = false
    /** Month to generate (YYYY-MM); defaults to last month. Statements are only ever created from here. */
    statementPeriod = (() => {
        const d = new Date()
        const last = new Date(d.getFullYear(), d.getMonth() - 1, 1)
        return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}`
    })()

    /** Close the chosen month and create statements. Nothing runs automatically. */
    async generateStatements() {
        if (!confirm(`Close ${this.statementPeriod} and create statements for every affiliate with fee shares due? Existing statements are not duplicated.`)) return
        this.generating = true
        try {
            const result = await this.affiliateService.adminGenerateStatements(this.statementPeriod)
            this.toastr.success(`${result.period}: ${result.statements} statement(s) created, R${result.total.toFixed(2)}`)
            await this.loadRequests()
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not generate statements')
        } finally {
            this.generating = false
        }
    }

    // ---- Statements ----
    async loadRequests() {
        this.requestsLoading = true
        try {
            this.requests = await this.affiliateService.adminPayoutRequests()
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not load statements')
        } finally {
            this.requestsLoading = false
        }
    }

    get visibleRequests() {
        return this.requestFilter === 'open' ? this.requests.filter((r) => r.status === 'requested' || r.status === 'approved') : this.requests
    }

    get openRequestCount(): number {
        return this.requests.filter((r) => r.status === 'requested' || r.status === 'approved').length
    }

    toggle(id: string) {
        this.expanded.has(id) ? this.expanded.delete(id) : this.expanded.add(id)
    }

    async action(request: any, action: 'approve' | 'pay' | 'reject') {
        const extra: { reference?: string; reason?: string } = {}
        if (action === 'pay') {
            const reference = prompt(`EFT reference for R${request.amount.toFixed(2)} to ${request.affiliateName}:`)
            if (!reference) return
            extra.reference = reference
        }
        if (action === 'reject') {
            const reason = prompt('Reason for rejecting (its fee shares carry forward to next month\'s statement):')
            if (reason === null) return
            extra.reason = reason
        }
        try {
            await this.affiliateService.adminPayoutAction(request.id, action, extra)
            this.toastr.success(action === 'pay' ? 'Marked as paid' : action === 'approve' ? 'Approved' : 'Rejected')
            await this.loadRequests()
            this.reportLoaded = false
        } catch (error: any) {
            this.toastr.error(error?.message || 'Action failed')
        }
    }

    /** Lines in a request where another affiliate also had a claim: shows why we're paying this one. */
    lineOthers(line: any) {
        return (line.candidates || []).filter((c: any) => c.affiliateUid !== line.winnerAffiliateUid)
    }

    // ---- Reports ----
    async loadReport() {
        this.reportLoading = true
        try {
            const toEnd = new Date(this.to)
            toEnd.setHours(23, 59, 59, 999)
            const result = await this.affiliateService.adminReport(new Date(this.from).toISOString(), toEnd.toISOString())
            this.names = result.affiliates
            this.commissions = result.commissions
            this.reportLoaded = true
        } catch (error: any) {
            this.toastr.error(error?.message || 'Could not load the report')
        } finally {
            this.reportLoading = false
        }
    }

    name(uid: string | null): string {
        if (!uid) return '—'
        const a = this.names[uid]
        return a ? `${a.displayName} (${a.code})` : uid
    }

    linesFor(type: AffiliateReferralType) {
        return this.commissions.filter((c) => c.winnerType === type && PAYING_STATUSES.includes(c.status))
    }

    totalsFor(type: AffiliateReferralType): AffiliateTotal[] {
        const map = new Map<string, AffiliateTotal>()
        for (const c of this.linesFor(type)) {
            const uid = c.winnerAffiliateUid
            const t = map.get(uid) || {
                affiliateUid: uid,
                name: this.names[uid]?.displayName || uid,
                code: this.names[uid]?.code || '',
                invoices: 0,
                gross: 0,
                commission: 0,
                pending: 0,
                approved: 0,
                requested: 0,
                paid: 0,
            }
            t.invoices++
            t.gross += c.grossAmount
            t.commission += c.amount
            ;(t as any)[c.status] += c.amount
            map.set(uid, t)
        }
        return Array.from(map.values()).sort((a, b) => b.commission - a.commission)
    }

    sum(rows: any[], field: string): number {
        return rows.reduce((total, r) => total + (r[field] || 0), 0)
    }

    get overlapLines() {
        return this.commissions.filter((c) => c.hasOverlap || (c.candidates || []).some((x: any) => x.outcome !== AffiliateCandidateOutcome.EARNED))
    }

    get overlapByBuilding(): BuildingOverlap[] {
        const map = new Map<string, BuildingOverlap>()
        for (const c of this.overlapLines) {
            const k = c.parkingKey || 'unknown'
            const b = map.get(k) || { parkingKey: k, building: c.denormParkingTitle || k, portfolio: c.denormMerchantCompanyName || '', invoices: 0, landlordAffiliates: [], parkerAffiliates: [], paid: 0, avoided: 0 }
            b.invoices++
            b.paid += c.amount || 0
            for (const cand of c.candidates || []) {
                const list = cand.type === AffiliateReferralType.BUILDING ? b.landlordAffiliates : b.parkerAffiliates
                const n = this.name(cand.affiliateUid)
                if (!list.includes(n)) list.push(n)
                if (cand.outcome !== AffiliateCandidateOutcome.EARNED) b.avoided += cand.amount || 0
            }
            map.set(k, b)
        }
        return Array.from(map.values()).sort((a, b) => b.avoided - a.avoided)
    }

    get totalAvoided(): number {
        return this.overlapByBuilding.reduce((sum, b) => sum + b.avoided, 0)
    }

    /** Should be zero: the commission doc id is the invoice key, and only one candidate can be earned. */
    get doubleEarnedCount(): number {
        return this.commissions.filter((c) => (c.candidates || []).filter((x: any) => x.outcome === AffiliateCandidateOutcome.EARNED).length > 1).length
    }

    candidate(c: any, type: AffiliateReferralType) {
        return (c.candidates || []).find((x: any) => x.type === type)
    }

    // ---- CSV ----
    exportCsv(kind: 'parker' | 'building' | 'overlap') {
        const lines = kind === 'overlap' ? this.overlapLines : this.linesFor(kind as AffiliateReferralType)
        const header = [
            'Payment date',
            'Invoice',
            'Building',
            'Portfolio',
            'Parker',
            'Invoice amount (incl VAT)',
            'Rental excl VAT',
            'ParkUpp fee %',
            'ParkUpp fee',
            'Banking fee',
            'Trading income',
            'Paid to',
            'Type',
            'Fee share',
            'Status',
            'Building claim',
            'Parker claim',
        ]
        const claim = (c: any, type: AffiliateReferralType) => {
            const x = this.candidate(c, type)
            return x ? `${this.name(x.affiliateUid)}: R${x.amount.toFixed(2)} ${AffiliateCandidateOutcomeLabels[x.outcome as AffiliateCandidateOutcome]}` : ''
        }
        const rows = lines.map((c) => [
            c.paymentDate.slice(0, 10),
            c.invoiceNumber || c.invoiceKey,
            c.denormParkingTitle,
            c.denormMerchantCompanyName,
            c.denormClientName,
            c.grossAmount.toFixed(2),
            (c.rentalExclVat ?? '').toString(),
            c.platformCommissionPct,
            (c.platformFee ?? '').toString(),
            (c.bankingFee ?? '').toString(),
            c.basisAmount.toFixed(2),
            this.name(c.winnerAffiliateUid),
            c.winnerType || 'none',
            (c.amount || 0).toFixed(2),
            c.status,
            claim(c, AffiliateReferralType.BUILDING),
            claim(c, AffiliateReferralType.PARKER),
        ])
        const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n')
        const link = document.createElement('a')
        link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
        link.download = `affiliate-${kind}-${this.from}-to-${this.to}.csv`
        link.click()
        URL.revokeObjectURL(link.href)
    }

    // ---- Integrity ----
    async runIntegrity(fix: boolean) {
        if (fix && !confirm('Create missing commission records and reverse stale ones?')) return
        this.integrityRunning = true
        try {
            this.integrity = await this.affiliateService.adminIntegrityCheck(new Date(this.integrityFrom).toISOString(), fix)
            if (fix) {
                this.toastr.success(`${this.integrity.fixed} records fixed`)
                this.reportLoaded = false
            }
        } catch (error: any) {
            this.toastr.error(error?.message || 'Integrity check failed')
        } finally {
            this.integrityRunning = false
        }
    }
}
